import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { vendorFetch } from '../../contexts/VendorAuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Loader2, KeyRound } from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';

export default function VendorForgotPassword() {
  const [tab, setTab] = useUrlTab('tab', 'link');
  // Link tab state
  const [linkEmail, setLinkEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  // OTP tab state
  const [otpEmail, setOtpEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpDone, setOtpDone] = useState(false);

  const submitLink = async (e) => {
    e.preventDefault();
    setLinkLoading(true);
    try {
      await vendorFetch('/partner/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: linkEmail.trim().toLowerCase() }),
      });
    } catch { /* Always show success for security */ }
    setLinkSent(true);
    setLinkLoading(false);
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    try {
      await vendorFetch('/partner/auth/request-otp', {
        method: 'POST',
        body: JSON.stringify({ email: otpEmail.trim().toLowerCase() }),
      });
      setOtpSent(true);
    } catch { setOtpSent(true); } // always show success
    setOtpLoading(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setOtpLoading(true);
    try {
      await vendorFetch('/partner/auth/verify-otp-reset', {
        method: 'POST',
        body: JSON.stringify({ email: otpEmail.trim().toLowerCase(), otp: otp.trim(), new_password: newPw }),
      });
      setOtpDone(true);
      toast.success('Password updated! You can now log in.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired OTP');
    }
    setOtpLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col items-center justify-center px-4 py-10" data-testid="forgot-password-page">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-8 space-y-5">
        {/* Logo */}
        <div className="text-center mb-1">
          <img src="/ellora-logo-blue.png" alt="Ellora EPC" className="h-9 w-auto mx-auto mb-3" />
          <div className="font-bold text-base text-slate-900">Reset Password</div>
          <div className="text-xs text-slate-400">Ellora EPC Vendor Portal</div>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-slate-100 rounded-sm p-0.5">
          {[['link', 'Reset via Link'], ['otp', 'Reset via OTP']].map(([id, lbl]) => (
            <button key={id} onClick={() => setTab(id)} type="button"
              className={`flex-1 py-2 text-xs font-bold rounded-sm transition-colors ${tab === id ? 'bg-white text-[#3b4872] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${id}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Link tab */}
        {tab === 'link' && (
          linkSent ? (
            <div className="text-center space-y-3 py-2">
              <Mail size={36} className="text-[#3b4872] mx-auto" />
              <div className="font-bold text-slate-900">Check your email</div>
              <p className="text-sm text-slate-600">
                If <strong>{linkEmail}</strong> is registered, a reset link has been sent. The link expires in 1 hour.
              </p>
              <Link to="/partner/login" className="text-[#3b4872] text-sm font-bold hover:underline">← Back to Login</Link>
            </div>
          ) : (
            <form onSubmit={submitLink} className="space-y-3">
              <p className="text-xs text-slate-500">Enter your registered email and we'll send a reset link.</p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email Address</label>
                <input type="email" value={linkEmail} onChange={e => setLinkEmail(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#3b4872]"
                  placeholder="you@company.com" required data-testid="forgot-pw-email" />
              </div>
              <button type="submit" disabled={linkLoading}
                className="w-full bg-[#3b4872] hover:bg-[#2d3856] text-white py-2.5 rounded font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-link-btn">
                {linkLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                {linkLoading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>
          )
        )}

        {/* OTP tab */}
        {tab === 'otp' && (
          otpDone ? (
            <div className="text-center space-y-3 py-2">
              <KeyRound size={36} className="text-emerald-500 mx-auto" />
              <div className="font-bold text-slate-900">Password updated!</div>
              <Link to="/partner/login" className="block bg-[#3b4872] text-white py-2.5 rounded font-bold text-sm hover:bg-[#2d3856]">Sign In →</Link>
            </div>
          ) : !otpSent ? (
            <form onSubmit={sendOtp} className="space-y-3">
              <p className="text-xs text-slate-500">Enter your email to receive a 6-digit one-time password.</p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email Address</label>
                <input type="email" value={otpEmail} onChange={e => setOtpEmail(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#3b4872]"
                  placeholder="you@company.com" required data-testid="otp-email" />
              </div>
              <button type="submit" disabled={otpLoading}
                className="w-full bg-[#3b4872] text-white py-2.5 rounded font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {otpLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                {otpLoading ? 'Sending…' : 'Send OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-3">
              <p className="text-xs text-slate-500">Enter the 6-digit OTP sent to <strong>{otpEmail}</strong>. Expires in 15 minutes.</p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">OTP</label>
                <input type="text" value={otp} onChange={e => setOtp(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm text-center tracking-widest font-bold focus:outline-none focus:border-[#3b4872]"
                  placeholder="123456" maxLength={6} required data-testid="otp-input" />
              </div>
              {[['new-password', 'New Password', newPw, setNewPw], ['confirm-password', 'Confirm Password', confirmPw, setConfirmPw]].map(([id, lbl, val, setVal]) => (
                <div key={id}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{lbl}</label>
                  <input type="password" value={val} onChange={e => setVal(e.target.value)} minLength={8}
                    className="w-full border border-slate-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#3b4872]"
                    required data-testid={id} />
                </div>
              ))}
              <button type="submit" disabled={otpLoading}
                className="w-full bg-[#3b4872] text-white py-2.5 rounded font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {otpLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                {otpLoading ? 'Verifying…' : 'Update Password'}
              </button>
              <button type="button" onClick={() => setOtpSent(false)} className="w-full text-xs text-slate-400 hover:text-slate-600">
                Resend OTP
              </button>
            </form>
          )
        )}

        <div className="border-t pt-3 text-center">
          <Link to="/partner/login" className="text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1">
            <ArrowLeft size={12} /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
